import React, { useEffect, useState } from 'react';

import { Botao, Field, Input, Modal } from '../../../ui';
import { formatPlaca, placaValida } from '../../../utils/mascaras';

interface ModalInformarPlacaProps {
  aberto: boolean;
  /** Linha de contexto: de qual veículo/associado se trata. */
  descricao: string;
  processando?: boolean;
  onConfirmar: (dados: { placa: string; renavam?: string }) => void;
  onCancelar: () => void;
}

/**
 * Informar a placa de um veículo 0km (at-81).
 *
 * O veículo foi cadastrado sem placa e sem renavam — o SAAC o identifica pelo
 * chassi até aqui. Esta é a única forma de trocar a identificação provisória pela
 * placa real, e o backend reenvia o cadastro ao SGA quando o veículo já está lá.
 *
 * ⚠️ O renavam é OPCIONAL: a placa costuma sair antes do documento, e exigir os
 * dois juntos travaria o registro da placa.
 */
const ModalInformarPlaca: React.FC<ModalInformarPlacaProps> = ({
  aberto,
  descricao,
  processando,
  onConfirmar,
  onCancelar,
}) => {
  const [placa, setPlaca] = useState('');
  const [renavam, setRenavam] = useState('');
  const [tentou, setTentou] = useState(false);

  // Limpa ao reabrir: a placa de um veículo não pode vazar para o seguinte.
  useEffect(() => {
    if (aberto) {
      setPlaca('');
      setRenavam('');
      setTentou(false);
    }
  }, [aberto]);

  const valida = placaValida(placa);
  const erroPlaca = tentou && !valida ? 'Use o formato AAA1234 ou o Mercosul AAA1A23.' : undefined;

  const confirmar = () => {
    setTentou(true);
    if (!valida) return;
    onConfirmar({ placa, ...(renavam.trim() ? { renavam: renavam.trim() } : {}) });
  };

  return (
    <Modal
      aberto={aberto}
      titulo="Informar placa"
      descricao={descricao}
      ocupado={processando}
      aoFechar={onCancelar}
      alinharRodape="direita"
      rodape={
        <>
          <Botao variante="secundario" disabled={processando} onClick={onCancelar}>
            Cancelar
          </Botao>
          <Botao variante="primario" carregando={processando} disabled={processando} onClick={confirmar}>
            Registrar placa
          </Botao>
        </>
      }
    >
      <div className="tw-flex tw-flex-col tw-gap-4">
        <Field rotulo="Placa" obrigatorio erro={erroPlaca} campo="placa">
          <Input
            type="text"
            autoComplete="off"
            value={placa}
            placeholder="ABC1D23"
            onChange={(evento) => setPlaca(formatPlaca(evento.target.value))}
          />
        </Field>
        <Field
          rotulo="Renavam"
          campo="renavam"
          dica="Opcional — se o documento ainda não saiu, deixe em branco."
        >
          <Input
            type="text"
            inputMode="numeric"
            autoComplete="off"
            maxLength={11}
            value={renavam}
            onChange={(evento) => setRenavam(evento.target.value.replace(/\D/g, ''))}
          />
        </Field>
        <p className="tw-m-0 tw-text-xs tw-text-texto-suave">
          A placa substitui a identificação provisória do 0km. Se o veículo já está no SGA,
          o cadastro de lá é atualizado na hora.
        </p>
      </div>
    </Modal>
  );
};

export default ModalInformarPlaca;
